import React, { useState, useEffect } from 'react';
import { Link, Switch, Redirect, Route } from 'react-router-dom';
import { Layout, Row, Col, Icon, message, Modal, Menu } from 'antd';

import {
  UserManagementContainer as UserManagementPage,
  LicensePageContainer as LicensePage,
  NotificationTableContainer
} from '../../containers';
import { HeaderRightContent } from '../header';

import styles from './Layout.module.scss';

const { Header, Sider, Content } = Layout;

const SideLayout = ({
  // user
  accessToken,
  isLoggedIn,
  isEmailValid,
  isAdmin,

  // license
  isFetchingFinished,
  isCreatingFinished,
  isApprovingFinished,
  isRejectingFinished,
  licenseError,

  // seed upload
  isSeedUploadingFinished,
  seedUploadError,

  // contract upload
  isContractFetchingFinished,
  isContractUploadingFinished,
  contractError,

  // admin
  isUserListFetchingFinished,
  isUserInfoUpdatingFinished,
  adminError,

  // actions
  fetchUserProfile,
  fetchLicenseStatusReset,
  createLicenseStatusReset,
  approveLicenseStatusReset,
  rejectLicenseStatusReset,
  uploadSeedStatusReset,
  fetchContractStatusReset,
  uploadContractStatusReset,
  fetchUserListStatusReset,
  updateUserInfoStatusReset
}) => {
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (isLoggedIn && isEmailValid && accessToken) {
      fetchUserProfile();
    }
  }, [isLoggedIn, isEmailValid, accessToken, fetchUserProfile]);

  const toggleCollapsed = () => setCollapsed(!collapsed);

  if (isLoggedIn) {
    if (licenseError && licenseError.response && licenseError.response.status === 401) {
      const errorModal = Modal.error({
        title: 'Session expired',
        content: 'Your session is expired. Please refresh the browser or login again.',
        okText: 'Get it',
        onOk: () => errorModal.destroy()
      });
    }

    if (isFetchingFinished && licenseError) {
      if (licenseError.data) {
        message.error(licenseError.data.Message || 'Failed to fetch license requests.');
      }

      fetchLicenseStatusReset();
    } else if (isFetchingFinished) {
      message.success('The license requests has been fetched.');
      fetchLicenseStatusReset();
    }

    if (isCreatingFinished && licenseError) {
      if (licenseError.data) {
        message.error(licenseError.data.Message || 'Failed to create license request.');
      }

      createLicenseStatusReset();
    } else if (isCreatingFinished) {
      message.success('The license request has been created.');
      createLicenseStatusReset();
    }

    if (isApprovingFinished && licenseError) {
      if (licenseError.data) {
        message.error(licenseError.data.Message || 'Failed to approve license request.');
      }

      approveLicenseStatusReset();
    } else if (isApprovingFinished) {
      message.success('The license request has been approved.');
      approveLicenseStatusReset();
    }

    if (isRejectingFinished && licenseError) {
      if (licenseError.data) {
        message.error(licenseError.data.Message || 'Failed to reject license request.');
      }

      rejectLicenseStatusReset();
    } else if (isRejectingFinished) {
      message.success('The license request has been rejected.');
      rejectLicenseStatusReset();
    }

    if (isSeedUploadingFinished && seedUploadError) {
      if (seedUploadError.data) {
        message.error(seedUploadError.data.Result || 'Failed to upload seed file.');
      }

      uploadSeedStatusReset();
    } else if (isSeedUploadingFinished) {
      message.success('The seed file has been uploaded.');
      uploadSeedStatusReset();
    }

    if (isContractFetchingFinished && contractError) {
      if (contractError.data) {
        message.error(contractError.data.Message || 'Failed to fetch contract file.');
      }

      fetchContractStatusReset();
    } else if (isContractFetchingFinished) {
      message.success('The contract file has been fetched.');
      fetchContractStatusReset();
    }

    if (isContractUploadingFinished && contractError) {
      if (contractError.data) {
        message.error(contractError.data.Result || 'Failed to upload contract file.');
      }

      uploadContractStatusReset();
    } else if (isContractUploadingFinished) {
      message.success('The contract file has been uploaded.');
      uploadContractStatusReset();
    }

    if (isUserListFetchingFinished && adminError) {
      if (adminError.data) {
        message.error(adminError.data.Message || 'Failed to fetch user data.');
      }

      fetchUserListStatusReset();
    } else if (isUserListFetchingFinished) {
      message.success('The user data has been fetched.');
      fetchUserListStatusReset();
    }

    if (isUserInfoUpdatingFinished && adminError) {
      if (adminError.data) {
        message.error(adminError.data.Message || 'Failed to update user info.');
      }

      updateUserInfoStatusReset();
    } else if (isUserInfoUpdatingFinished) {
      message.success('The user info has been updated.');
      updateUserInfoStatusReset();
    }
  }

  const layout = (
    <Layout>
      <Sider className={styles.sidebar} trigger={null} collapsible collapsed={collapsed}>
        <div className={collapsed ? styles.logoContainerCollapse : styles.logoContainer}>
          <Link to="/dashboard">
            <div className={styles.logo} />
          </Link>
        </div>

        <Menu theme="dark" mode="inline" defaultSelectedKeys={['dashboard']}>
          <Menu.Item key="dashboard">
            <Link to="/license">
              <Icon type="file-text" />
              <span>License List</span>
            </Link>
          </Menu.Item>

          {isAdmin && (
            <Menu.Item key="notifications">
              <Link to="/notifications">
                <Icon type="notification" />
                <span>Notification History</span>
              </Link>
            </Menu.Item>
          )}

          {isAdmin && (
            <Menu.Item key="admin">
              <Link to="/admin/users">
                <Icon type="user" />
                <span>User Management</span>
              </Link>
            </Menu.Item>
          )}
        </Menu>
      </Sider>

      <Layout className={styles.mainLayout}>
        <Header className={styles.header}>
          <Row type="flex" justify="space-between" align="top">
            <Col>
              <Icon
                className="side-layout-trigger"
                type={collapsed ? 'menu-unfold' : 'menu-fold'}
                onClick={toggleCollapsed}
              />
            </Col>
            <Col>
              <HeaderRightContent />
            </Col>
          </Row>
        </Header>
        <Content className={styles.content}>
          <Switch>
            <Route path={'/license'} exact component={LicensePage} />
            <Route
              path={'/notifications'}
              exact
              render={() =>
                isAdmin ? (
                  <NotificationTableContainer />
                ) : (
                  <div style={{ padding: 32, textAlign: 'center' }}>
                    You do not have permission to view this page.
                  </div>
                )
              }
            />
            <Route
              path={'/admin/users'}
              exact
              render={() => (isAdmin ? <UserManagementPage /> : <Redirect to={'/license'} />)}
            />

            <Redirect to={'/license'} />
          </Switch>
        </Content>
      </Layout>
    </Layout>
  );

  return isLoggedIn && isEmailValid ? layout : <Redirect to="/login" />;
};

export default SideLayout;
